/**
 * Content that names a different product than the row it sits on (MOT-474).
 *
 * A scrape that lands on the wrong page keeps the row's own name, SKU and
 * barcode but fills the copy, spec table and In The Box from the other
 * product: the Fujifilm XC13-33mm carried the XF55-200mm's description and
 * specs (84-305mm, 580 g), the XF56mm F1.2 R WR the XF23mm's box list. These
 * checks read only the row, so they run wherever readiness runs and send the
 * row to review instead of the catalog.
 *
 * Pure, no imports: `frontend/src/utils/catalogContentConsistency.ts` is a
 * byte-identical copy (pinned by `catalogContentConsistency.test.ts`).
 */

/** Text fields are `unknown` because import payloads sometimes hold objects there. */
export type ContentConsistencyInput = {
  name?: unknown;
  model?: unknown;
  productType?: unknown;
  description?: unknown;
  aiDescription?: unknown;
  shortDescription?: unknown;
  aiShortDescription?: unknown;
  specifications?: unknown;
  specificationsRaw?: unknown;
  inTheBox?: unknown;
};

export type ContentConsistencyIssue = {
  field: string;
  /** The text that names the other product. */
  found: string;
  message: string;
};

type LensMention = { focal: string; text: string };

/** "55", "55-200", "70 to 200", "14mm – 30": one focal length or a zoom range. */
const FOCAL = '(\\d{1,4}(?:\\.\\d+)?)(?:\\s*(?:mm)?\\s*(?:[-–—]|to)\\s*(\\d{1,4}(?:\\.\\d+)?))?';

/** Spec labels whose value is the product's own model name. */
const MODEL_SPEC_LABEL = /^(model|model name|model number|product name|lens name|type)$/i;
const FOCAL_SPEC_LABEL = /^focal length$/i;

function text(value: unknown): string {
  return String(value ?? '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim();
}

function focalKey(a: string, b?: string): string {
  const n = (v: string) => String(Number(v));
  return b ? `${n(a)}-${n(b)}` : n(a);
}

/**
 * Lens model names in a text: a focal length followed by its f-number, as
 * lens names are written ("XF55-200mmF3.5-4.8", "24-70mm f/2.8"). A bare
 * "84-305mm" (an equivalent focal range) is not a lens name.
 */
export function lensModelMentions(raw: string): LensMention[] {
  const out: LensMention[] = [];
  const re = new RegExp(`${FOCAL}\\s*mm\\s*(?:f\\s*\\/?\\s*|1\\s*:\\s*)(\\d{1,2}(?:\\.\\d+)?)`, 'gi');
  const value = text(raw);
  let m: RegExpExecArray | null;
  while ((m = re.exec(value)) !== null) {
    out.push({ focal: focalKey(m[1], m[2]), text: m[0] });
  }
  return out;
}

/** Every focal length in a text ("56mm", "13-33mm"), equivalents included. */
export function focalLengthKeys(raw: string): string[] {
  const keys = new Set<string>();
  const re = new RegExp(`${FOCAL}\\s*mm\\b`, 'gi');
  const value = text(raw);
  let m: RegExpExecArray | null;
  while ((m = re.exec(value)) !== null) keys.add(focalKey(m[1], m[2]));
  return Array.from(keys);
}

/** The focal lengths that name this lens, from its own name and model. */
export function ownLensFocalKeys(input: Pick<ContentConsistencyInput, 'name' | 'model'>): string[] {
  const keys = new Set<string>();
  for (const v of [input.name, input.model]) {
    for (const m of lensModelMentions(text(v))) keys.add(m.focal);
  }
  if (keys.size === 0) {
    for (const v of [input.name, input.model]) for (const k of focalLengthKeys(text(v))) keys.add(k);
  }
  return Array.from(keys);
}

/** A standalone lens: typed as one, or named like one and not a camera kit. */
function isStandaloneLens(input: ContentConsistencyInput): boolean {
  const hay = `${text(input.name)} ${text(input.model)}`;
  // A kit or a multi-lens bundle names several lenses by design.
  if (/\b(kit|camera|body|bundle|set|with)\b/i.test(hay)) return false;
  // An accessory named after the lens it fits ("Fisheye Converter for FE 28mm F2").
  if (/\b(converter|teleconverter|extender|adapter|hood|cap|filter|for)\b/i.test(hay)) return false;
  const type = text(input.productType).toLowerCase();
  if (type === 'lens') return true;
  if (type && type !== 'other' && type !== 'accessory') return false;
  return lensModelMentions(hay).length > 0;
}

/** First sentence: where copy names the product it describes. */
function leadSentence(raw: string): string {
  const value = text(raw).replace(/<[^>]*>/g, ' ').trim();
  const m = value.match(/^.{0,400}?[.!?](?=\s|$)/);
  return (m ? m[0] : value.slice(0, 400)).trim();
}

/** `{ section: { label: value } }` or `{ label: value }` flattened to label/value pairs. */
function specPairs(specs: unknown): Array<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  if (!specs || typeof specs !== 'object' || Array.isArray(specs)) return out;
  for (const [key, value] of Object.entries(specs as Record<string, unknown>)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [label, inner] of Object.entries(value as Record<string, unknown>)) {
        if (inner == null || typeof inner === 'object') continue;
        out.push({ label: label.replace(/_/g, ' ').trim(), value: text(inner) });
      }
    } else if (value != null && typeof value !== 'object') {
      out.push({ label: key.replace(/_/g, ' ').trim(), value: text(value) });
    }
  }
  return out;
}

function boxItemName(item: unknown): string {
  if (typeof item === 'string') return text(item);
  if (item && typeof item === 'object') {
    const row = item as { name?: unknown; isAccessory?: unknown };
    return row.isAccessory === true ? '' : text(row.name);
  }
  return '';
}

function focalBounds(key: string): [number, number] {
  const [a, b] = key.split('-').map(Number);
  return [a, b ?? a];
}

/**
 * Two focal lengths that could describe the same lens: equal, or overlapping
 * ranges ("17mm" for a 17-40mm, a "15-30mm" equivalent for a 10-20mm). Only
 * focal lengths that cannot belong to this lens count as another product.
 */
function focalOverlaps(a: string, b: string): boolean {
  const [a1, a2] = focalBounds(a);
  const [b1, b2] = focalBounds(b);
  return a1 <= b2 && b1 <= a2;
}

function overlapsOwn(key: string, own: Set<string>): boolean {
  return Array.from(own).some((k) => focalOverlaps(k, key));
}

/** A text names a lens, and none of the lenses it names can be this one. */
function namesOtherLens(raw: string, own: Set<string>): LensMention | null {
  const mentions = lensModelMentions(raw);
  if (mentions.length === 0) return null;
  if (mentions.some((m) => overlapsOwn(m.focal, own))) return null;
  return mentions[0];
}

/**
 * Places where a lens row's content names another lens. Empty for products
 * the checks cannot judge (cameras, kits, accessories, a lens with no focal
 * length in its own name), so a row is never held on a guess.
 */
export function catalogContentConsistencyIssues(input: ContentConsistencyInput): ContentConsistencyIssue[] {
  if (!isStandaloneLens(input)) return [];
  const own = new Set(ownLensFocalKeys(input));
  if (own.size === 0) return [];
  const ownLabel = text(input.model) || text(input.name);
  const issues: ContentConsistencyIssue[] = [];
  const seen = new Set<string>();
  const add = (field: string, found: string) => {
    const key = `${field}:${found}`;
    if (seen.has(key)) return;
    seen.add(key);
    issues.push({
      field,
      found,
      message: `${field} names ${found}, not ${ownLabel}`,
    });
  };

  for (const field of ['description', 'aiDescription', 'shortDescription', 'aiShortDescription'] as const) {
    const hit = namesOtherLens(leadSentence(text(input[field])), own);
    if (hit) add(field, hit.text);
  }

  for (const source of ['specifications', 'specificationsRaw'] as const) {
    for (const { label, value } of specPairs(input[source])) {
      if (!value || value.length > 120) continue;
      if (MODEL_SPEC_LABEL.test(label)) {
        const hit = namesOtherLens(value, own);
        if (hit) add(`spec "${label}"`, hit.text);
      } else if (FOCAL_SPEC_LABEL.test(label)) {
        const keys = focalLengthKeys(value);
        if (keys.length > 0 && !keys.some((k) => overlapsOwn(k, own))) add(`spec "${label}"`, value);
      }
    }
  }

  if (Array.isArray(input.inTheBox)) {
    for (const item of input.inTheBox) {
      const name = boxItemName(item);
      if (!name) continue;
      const hit = namesOtherLens(name, own);
      if (hit) add('In The Box', hit.text);
    }
  }

  return issues;
}

/**
 * True when two texts each name a lens and none of the lenses match: a shop
 * or specs page that is another lens's page. False whenever either side names
 * no lens, so a page that cannot be judged is never dropped.
 */
export function lensNamesConflict(own: string, other: string): boolean {
  const ownKeys = ownLensFocalKeys({ name: own });
  if (ownKeys.length === 0) return false;
  const mentions = lensModelMentions(other);
  if (mentions.length === 0) return false;
  return !mentions.some((m) => ownKeys.some((k) => focalOverlaps(k, m.focal)));
}
